// =============================================================================
// features/configuracoes/comercial/ComercialSettingsPage.tsx — Rota
// `/configuracoes/comercial` (F34-S06, doc 26 §6.9).
//
// Mesmo padrão de gate de features/sales/campaigns/CampaignsPage.tsx:
// permissão (`sales_campaigns:manage`) E feature flag
// (`sales.workspace.enabled`, a mesma que a API checa em
// apps/api/src/modules/sales/settings/routes.ts). Acesso sem qualquer um dos
// dois mostra um estado PRÓPRIO — nunca tela quebrada nem redirect
// surpresa. O conteúdo real (ComercialSettingsContent, que dispara as
// queries) só monta depois que os dois gates passam.
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import { ContextualHelp } from '../../help/contextual';

import { ComercialSettingsContent } from './ComercialSettingsContent';

const FLAG_KEY = 'sales.workspace.enabled';
const MANAGE_PERMISSION = 'sales_campaigns:manage';

export function ComercialSettingsPage(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FLAG_KEY);

  const canManage = hasPermission(MANAGE_PERMISSION);

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <Link
          to="/configuracoes"
          className="font-sans text-sm text-ink-3 hover:text-azul transition-colors flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 rounded-xs"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            className="w-4 h-4"
            aria-hidden="true"
          >
            <path d="M10 4l-4 4 4 4" />
          </svg>
          Configurações
        </Link>
        <span className="text-ink-4 text-sm">/</span>
        <span className="font-sans text-sm text-ink">Comercial</span>
      </div>

      {/* Header */}
      <div>
        <div className="flex items-center gap-1">
          <h1
            className="font-display font-bold text-ink"
            style={{
              fontSize: 'var(--text-3xl)',
              letterSpacing: '-0.04em',
              fontVariationSettings: "'opsz' 48",
            }}
          >
            Configuração comercial
          </h1>
          <ContextualHelp
            featureKey="sales.settings.edit"
            permission={MANAGE_PERMISSION}
            className="ml-0.5"
          />
        </div>
        <p className="mt-1 font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
          Fuso, janelas de contato, desfechos, tentativas e distribuição — sem precisar de PATCH
          manual.
        </p>
      </div>

      {!canManage ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-full"
            style={{ background: 'var(--surface-muted)' }}
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              className="w-6 h-6"
              style={{ color: 'var(--text-4)' }}
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
            </svg>
          </div>
          <p className="font-sans text-sm max-w-xs" style={{ color: 'var(--text-3)' }}>
            Você não tem acesso à configuração comercial. Fale com um administrador se precisar de
            acesso.
          </p>
        </div>
      ) : flagLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 rounded-md animate-pulse"
              style={{ background: 'var(--surface-muted)' }}
              aria-hidden="true"
            />
          ))}
        </div>
      ) : !flagEnabled ? (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-sm border"
          style={{
            background: 'var(--warning-bg)',
            borderColor: 'var(--warning)',
            borderLeftWidth: 3,
          }}
          role="alert"
        >
          <div>
            <p className="font-sans text-sm font-semibold text-ink">Módulo desabilitado</p>
            <p className="font-sans text-xs text-ink-3 mt-0.5">
              A flag <code className="font-mono">{FLAG_KEY}</code> está desativada para esta
              organização.
            </p>
          </div>
        </div>
      ) : (
        <ComercialSettingsContent />
      )}
    </div>
  );
}
